/** Facade that provides a high-level api in order to interact with mercure protocol */

import { MERCURE_CLIENT_EVENTS, createMercureUpdate } from '~/composables/mercure';
import type { ConnectionConfig, ConnectionState, MercureUpdate, Subscription, SubscriptionId, UpdateHandler } from '~/composables/mercure';
import type { ConnectionManager } from '~/composables/mercure/infrastructure/connection/ConnectionManager';
import type { SubscriptionManager } from '~/composables/mercure/application/services/SubscriptionManager';
import type { SafeUpdateValidator } from '~/composables/mercure/infrastructure/validation/SafeUpdateValidator';
import { ConnectionRefusedError } from '~/composables/mercure/infrastructure/connection/EventSourceAdapter';

export interface SubscriptionConfig<T = unknown> {
  token: string;
  topics: string | string[];
  handler: UpdateHandler<T>;
}

export interface MercureClientConfig {
  hubUrl: string;
  debug?: boolean;
  /** Fetches a new subscriber token when the hub refuses an expired one. */
  refreshToken?: () => Promise<string | null>;
}

const HTTP_UNAUTHORIZED = 401;

function isUnauthorized (error: unknown): error is ConnectionRefusedError {
  return error instanceof ConnectionRefusedError && error.status === HTTP_UNAUTHORIZED;
}

type ClientEventListener<T = unknown> = (payload: T) => void;

export class MercureClient {
  private readonly config: MercureClientConfig;
  private readonly connectionManager: ConnectionManager;
  private readonly subscriptionManager: SubscriptionManager;
  private readonly updateValidator: SafeUpdateValidator;
  private readonly eventListeners: Map<string, Set<ClientEventListener>>;
  private isInitialized: boolean = false;
  private currentToken: string | null = null;
  private tokenRefresh: Promise<void> | null = null;
  private lastSync: Promise<void> = Promise.resolve();
  private unprovenToken: string | null = null;

  constructor (
    config: MercureClientConfig,
    connectionManager: ConnectionManager,
    subscriptionManager: SubscriptionManager,
    updateValidator: SafeUpdateValidator,
  ) {
    this.config = config;
    this.connectionManager = connectionManager;
    this.subscriptionManager = subscriptionManager;
    this.updateValidator = updateValidator;
    this.eventListeners = new Map();
    this.setupConnectionListeners();
  }

  async subscribe<T = unknown>(
    config: SubscriptionConfig<T>,
  ): Promise<Subscription<T> & { unsubscribe: () => Promise<void> }> {
    const topicsArray: string[] = Array.isArray(config.topics) ? config.topics : [config.topics];

    const subscription: Subscription<T> = this.subscriptionManager.subscribe<T>(
      topicsArray,
      config.handler,
    );

    try {
      await this.syncConnectionTopics(config.token);
    } catch (error) {
      const recovered: boolean = isUnauthorized(error) && await this.reopenedWithFreshToken();
      if (error instanceof ConnectionRefusedError && !recovered) {
        this.subscriptionManager.unsubscribe(subscription.id);
        void this.syncConnectionTopics().catch((): void => {});
        throw error;
      }
    }

    this.emit(MERCURE_CLIENT_EVENTS.SUBSCRIPTION_CREATED, { subscriptionId: subscription.id, topics: topicsArray });

    return {
      ...subscription,
      unsubscribe: async (): Promise<void> => {
        await this.unsubscribe(subscription.id);
      },
    };
  }

  async unsubscribe (subscriptionId: SubscriptionId): Promise<void> {
    this.subscriptionManager.unsubscribe(subscriptionId);

    this.emit(MERCURE_CLIENT_EVENTS.SUBSCRIPTION_CANCELLED, { subscriptionId });

    await this.syncConnectionTopics();
  }

  async disconnect (): Promise<void> {
    this.isInitialized = false;
    this.currentToken = null;

    if (!this.connectionManager.getCurrentConfig()) {
      return;
    }

    await this.connectionManager.disconnect();

    this.emit(MERCURE_CLIENT_EVENTS.DISCONNECTED, {});
  }

  getConnectionState (): ConnectionState {
    return this.connectionManager.getState();
  }

  isConnected (): boolean {
    return this.connectionManager.isConnected();
  }

  getSubscriptionCount (): number {
    return this.subscriptionManager.getSubscriptionCount();
  }

  on<T = any>(event: string, listener: ClientEventListener<T>): () => void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }

    const listeners: Set<ClientEventListener> = this.eventListeners.get(event);
    if (listeners) {
      listeners.add(listener);
    }

    return (): void => {
      this.eventListeners.get(event)?.delete(listener);
    };
  }

  async connect (config: ConnectionConfig): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      await this.connectionManager.connect(config);
      this.isInitialized = true;

      this.emit(MERCURE_CLIENT_EVENTS.CONNECTED, { hubUrl: config.hubUrl });
    } catch (error) {
      this.emit(MERCURE_CLIENT_EVENTS.ERROR, { error });
      throw error;
    }
  }

  private syncConnectionTopics (tokenOverride?: string): Promise<void> {
    const sync: Promise<void> = this.lastSync.then(() => this.reconcileConnection(tokenOverride));
    this.lastSync = sync.catch((): void => {});

    return sync;
  }

  private async reconcileConnection (tokenOverride?: string): Promise<void> {
    const activeTopics: string[] = this.getActiveTopics();

    if (activeTopics.length === 0) {
      await this.disconnect();
      return;
    }

    const token: string = tokenOverride ?? this.currentToken;
    if (!token) {
      throw new Error('[MercureClient] Missing token to establish Mercure connection');
    }

    const desiredConfig: ConnectionConfig = {
      hubUrl: this.config.hubUrl,
      token,
      topics: activeTopics,
    };

    const currentConfig: ConnectionConfig = this.connectionManager.getCurrentConfig();
    const shouldReconnect: boolean =
      !currentConfig ||
      currentConfig.hubUrl !== desiredConfig.hubUrl ||
      currentConfig.token !== desiredConfig.token ||
      !this.areTopicsEqual(currentConfig.topics, desiredConfig.topics);

    if (shouldReconnect) {
      if (currentConfig) {
        await this.connectionManager.disconnect();
        this.isInitialized = false;
      }

      this.currentToken = token;
      await this.connect(desiredConfig);
    }
  }

  private getActiveTopics (): string[] {
    const subscriptions: Subscription[] = this.subscriptionManager.getAllSubscriptions();
    const uniqueTopics: Set<string> = new Set<string>();

    for (const subscription of subscriptions) {
      for (const topic of subscription.topics) {
        uniqueTopics.add(topic);
      }
    }

    return Array.from(uniqueTopics);
  }

  private areTopicsEqual (left: readonly string[], right: readonly string[]): boolean {
    if (left.length !== right.length) {
      return false;
    }

    const leftSorted: string[] = [...left].sort();
    const rightSorted: string[] = [...right].sort();

    return leftSorted.every((value, index): boolean => value === rightSorted[index]);
  }

  private emit<T = any>(event: string, data: T): void {
    const listeners: Set<ClientEventListener> = this.eventListeners.get(event);
    if (!listeners || listeners.size === 0) {
      return;
    }

    for (const listener of listeners) {
      try {
        listener(data);
      } catch (error) {
        if (this.config.debug) {
          console.error(`[MercureClient] Event listener error for "${event}":`, error);
        }
      }
    }
  }

  private setupConnectionListeners (): void {
    this.connectionManager.onMessage(async (event: MessageEvent): Promise<void> => {
      try {
        const update: MercureUpdate = this.parseUpdate(event);
        if (update) {
          const count: number = await this.subscriptionManager.dispatch(update);

          if (this.config.debug) {
            /** @eslint-disable-next-line no-console */
            console.info(`[MercureClient] Dispatched update to ${count} subscription(s)`);
          }

          this.emit(MERCURE_CLIENT_EVENTS.UPDATE_RECEIVED, { update, notifiedCount: count });
        }
      } catch (error) {
        if (this.config.debug) {
          console.error('[MercureClient] Error processing message:', error);
        }

        this.emit(MERCURE_CLIENT_EVENTS.ERROR, { error });
      }
    });

    this.connectionManager.onStateChange((state: ConnectionState): void => {
      if (state.status === 'connected') {
        this.unprovenToken = null;
      }
      this.emit(MERCURE_CLIENT_EVENTS.CONNECTION_STATE_CHANGED, { state });
    });

    this.connectionManager.onError((error: Error): void => {
      this.emit(MERCURE_CLIENT_EVENTS.ERROR, { error });

      if (isUnauthorized(error)) {
        void this.reconnectWithFreshToken();
      }
    });
  }

  private async reopenedWithFreshToken (): Promise<boolean> {
    await this.reconnectWithFreshToken();

    return this.connectionManager.isConnected();
  }

  private reconnectWithFreshToken (): Promise<void> {
    this.tokenRefresh ??= this.replaceRefusedToken().finally((): void => {
      this.tokenRefresh = null;
    });

    return this.tokenRefresh;
  }

  private async replaceRefusedToken (): Promise<void> {
    const refused: ConnectionConfig | null = this.connectionManager.getCurrentConfig();
    if (!this.config.refreshToken || !refused || refused.token === this.unprovenToken) {
      return;
    }

    try {
      const token: string | null = await this.config.refreshToken();
      if (!token || token === refused.token) {
        return;
      }

      this.unprovenToken = token;
      this.currentToken = token;
      await this.syncConnectionTopics();
    } catch (error) {
      this.emit(MERCURE_CLIENT_EVENTS.ERROR, { error });
    }
  }

  private parseUpdate (event: MessageEvent): MercureUpdate | null {
    try {
      interface MercureMessageEvent extends MessageEvent {
        id?: string;
        lastEventId: string;
        subscribedTopics?: string[];
      }

      const mercureEvent: MercureMessageEvent = event as MercureMessageEvent;

      if (this.config.debug) {
        /** @eslint-disable-next-line no-console */
        console.log('[MercureClient] Raw event:', {
          data: event.data,
          lastEventId: mercureEvent.lastEventId,
          id: mercureEvent.id,
          type: event.type,
          subscribedTopics: mercureEvent.subscribedTopics,
        });
      }

      let rawData: unknown;

      if (typeof event.data === 'string') {
        try {
          rawData = JSON.parse(event.data);
        } catch {
          rawData = { message: event.data };
        }
      } else {
        rawData = event.data;
      }

      const validationResult: ValidationResult = this.updateValidator.validate(rawData, {
        sanitize: true,
      });

      if (!validationResult.valid) {
        if (this.config.debug) {
          console.error('[MercureClient] Invalid update data:', validationResult.error);
        }
        this.emit('error', {
          error: new Error(`Invalid update: ${validationResult.error}`),
        });
        return null;
      }

      const data = validationResult.sanitizedData ?? rawData;

      const subscribedTopics: string[] = (mercureEvent.subscribedTopics ?? [])
        .filter((value): value is string => typeof value === 'string');

      const topicFromPayload: string | null = this.extractTopicFromPayload(data);
      const topic: string | undefined = topicFromPayload ?? subscribedTopics[0];

      if (!topic) {
        throw new Error('No topic found in update data or subscribed topics');
      }

      const id: string = mercureEvent.id ?? mercureEvent.lastEventId ?? String(Date.now());

      return createMercureUpdate({
        id,
        topic,
        data,
        receivedAt: new Date(),
      });
    } catch (error) {
      if (this.config.debug) {
        console.error('[MercureClient] Failed to parse update:', error);
      }
      this.emit('error', {
        error: error instanceof Error ? error : new Error(String(error)),
      });
      return null;
    }
  }

  private extractTopicFromPayload (data: unknown): string | null {
    if (!data || typeof data !== 'object') {
      return null;
    }

    const record = data as Record<string, unknown>;
    const topics = record.topics;

    if (Array.isArray(topics)) {
      const firstTopic = topics.find((value): value is string => typeof value === 'string' && value.trim().length > 0);
      if (firstTopic) {
        return firstTopic;
      }
    }

    if (typeof topics === 'string' && topics.trim().length > 0) {
      return topics;
    }

    return null;
  }
}
